import React from 'react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import {
  installCmd,
  reactProviderExample,
  directUsageExample,
  checkNetworkCmd,
  verifyAudioElement,
  websocketTest,
  stunTest,
  backendRegisterAgent,
  frontendIntegration,
  authFlowMermaid,
  credentialStorage,
  errorHandling,
  registerWithRetry,
  refreshCredentials
} from './webrtcCodeBlocks';

const codeBlock = (code) => (
  <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto mb-2"><code>{code}</code></pre>
);

const WebRTCClient = () => (
  <div className="min-h-screen bg-[#f6fbfa]">
    <Navbar />
    <Sidebar />
    <main className="lg:pl-32 pt-24 flex justify-center">
      <div className="w-full max-w-screen-md px-4 pb-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">🚀 Getting Started with TaarWebRTC</h1>
        <p className="mb-4">Welcome to TaarWebRTC! This guide will help you integrate our WebRTC client library into your application. You can use it either directly or through our React provider component.</p>
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">📦 Installation</h2>
        {codeBlock(installCmd)}
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">🌐 Required Domain Whitelisting</h2>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li><code>api.taar.ai</code> - For agent registration and WebRTC configuration</li>
          <li><code>clients.taar.ai</code> - For WebRTC signaling and media servers</li>
        </ul>
        <p className="mb-4">If you're behind a corporate firewall or using a VPN, make sure these domains are accessible.</p>
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">🔑 Understanding the Registration Flow</h2>
        <h3 className="font-semibold mt-4 mb-2">What is a Nonce? 🔐</h3>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2">
          <li>When TaarWebRTC initializes, it automatically fetches a nonce from <code>api.taar.ai</code></li>
          <li>This nonce is passed to your <code>onReady</code> callback</li>
          <li>You must use this nonce to register your agent with the backend</li>
          <li>The backend validates the nonce and returns credentials</li>
          <li>These credentials are used to establish a secure WebRTC connection</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Each registration attempt is unique</li>
          <li>Only authorized clients can register</li>
          <li>Registration requests can't be replayed</li>
        </ul>
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">🎯 Quick Start</h2>
        <h3 className="font-semibold mt-4 mb-2">Option 1: Using the Provider (Recommended for React Apps) 🎨</h3>
        {codeBlock(reactProviderExample)}
        <h3 className="font-semibold mt-4 mb-2">Option 2: Direct Usage (For Non-React Apps) 🔧</h3>
        {codeBlock(directUsageExample)}
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">🧩 Component Reference</h2>
        <h3 className="font-semibold mt-4 mb-2">TaarWebRTCProvider</h3>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li><b>debug</b> (boolean): Enable detailed logging</li>
          <li><b>onReady</b> (function): Called when client is ready to register</li>
          <li><b>onCallStarted</b> (function): Called when a call starts</li>
          <li><b>onCallEnded</b> (function): Called when a call ends</li>
          <li><b>onError</b> (function): Called when an error occurs</li>
          <li><b>onMute</b> (function): Called when microphone is muted</li>
          <li><b>onUnmute</b> (function): Called when microphone is unmuted</li>
          <li><b>onVolumeChange</b> (function): Called when volume levels change</li>
          <li><b>onDisconnect</b> (function): Called when connection is lost</li>
        </ul>
        <h3 className="font-semibold mt-4 mb-2">TaarWebRTCClientUI</h3>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li><b>agentCredentials</b> (string): Your agent credentials from registration</li>
        </ul>
        <h3 className="font-semibold mt-4 mb-2">useTaarWebRTC Hook</h3>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li><b>state</b>: Current WebRTC state
            <ul className="list-disc pl-6">
              <li><b>isRegistered</b> (boolean): Whether the client is registered</li>
              <li><b>isCallActive</b> (boolean): Whether there's an active call</li>
              <li><b>isMuted</b> (boolean): Whether the microphone is muted</li>
              <li><b>currentCall</b> (object): Information about the current call</li>
              <li><b>volume</b> (object): Current volume levels</li>
            </ul>
          </li>
          <li><b>makeCall</b>: Function to start a call</li>
          <li><b>endCall</b>: Function to end a call</li>
          <li><b>mute</b>: Function to mute microphone</li>
          <li><b>unmute</b>: Function to unmute microphone</li>
          <li><b>setVolume</b>: Function to adjust volume</li>
          <li><b>answerCall</b>: Function to answer incoming calls</li>
        </ul>
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">🔄 Client Lifecycle</h2>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2">
          <li><b>Initialization</b>: Create a new instance of <code>TaarWebRTC</code> or use <code>TaarWebRTCProvider</code>. The client will automatically fetch a nonce and call your <code>onReady</code> callback.</li>
          <li><b>Registration</b>: When <code>onReady</code> is called, you'll receive a nonce. Use this nonce to register your agent with the backend. Pass the returned credentials to the client.</li>
          <li><b>Making Calls</b>: Use <code>makeCall(destination)</code> to start a call. The destination should be a valid SIP URI (e.g., 'user@taar.ai').</li>
          <li><b>Call Controls</b>: <code>mute()</code> / <code>unmute()</code> - Control your microphone. <code>setVolume(&#123; input: number, output: number &#125;)</code> - Adjust volume levels. <code>endCall()</code> - End the current call.</li>
          <li><b>Cleanup</b>: Call <code>unregister()</code> when you're done to clean up resources.</li>
        </ol>
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">📡 Available Events</h2>
        <table className="min-w-full text-sm text-left mb-4">
          <thead>
            <tr className="border-b">
              <th className="pr-4 py-2 font-semibold">Event</th>
              <th className="pr-4 py-2 font-semibold">Description</th>
              <th className="pr-4 py-2 font-semibold">When it's called</th>
              <th className="pr-4 py-2 font-semibold">What to do</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b"><td className="pr-4 py-2">onReady</td><td className="pr-4 py-2">Client is ready to register</td><td className="pr-4 py-2">After initialization</td><td className="pr-4 py-2">Register your agent with the backend using the provided nonce</td></tr>
            <tr className="border-b"><td className="pr-4 py-2">onCallStarted</td><td className="pr-4 py-2">A call has started</td><td className="pr-4 py-2">When a call is initiated or received</td><td className="pr-4 py-2">Update UI to show call in progress</td></tr>
            <tr className="border-b"><td className="pr-4 py-2">onCallEnded</td><td className="pr-4 py-2">A call has ended</td><td className="pr-4 py-2">When a call is terminated</td><td className="pr-4 py-2">Update UI to show call ended</td></tr>
            <tr className="border-b"><td className="pr-4 py-2">onMute</td><td className="pr-4 py-2">Microphone is muted</td><td className="pr-4 py-2">When mute is enabled</td><td className="pr-4 py-2">Update UI to show muted state</td></tr>
            <tr className="border-b"><td className="pr-4 py-2">onUnmute</td><td className="pr-4 py-2">Microphone is unmuted</td><td className="pr-4 py-2">When mute is disabled</td><td className="pr-4 py-2">Update UI to show unmuted state</td></tr>
            <tr className="border-b"><td className="pr-4 py-2">onVolumeChange</td><td className="pr-4 py-2">Volume levels changed</td><td className="pr-4 py-2">When input/output volume changes</td><td className="pr-4 py-2">Update volume indicators</td></tr>
            <tr className="border-b"><td className="pr-4 py-2">onError</td><td className="pr-4 py-2">An error occurred</td><td className="pr-4 py-2">On any error</td><td className="pr-4 py-2">Handle the error appropriately</td></tr>
            <tr><td className="pr-4 py-2">onDisconnect</td><td className="pr-4 py-2">Connection lost</td><td className="pr-4 py-2">When WebRTC connection is lost</td><td className="pr-4 py-2">Attempt to reconnect or notify user</td></tr>
          </tbody>
        </table>
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">🔍 Detailed Troubleshooting Guide</h2>
        <h3 className="font-semibold mt-4 mb-2">1. Registration Issues</h3>
        <p className="mb-2 font-semibold">Symptoms:</p>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Client fails to register</li>
          <li><code>onError</code> is called with registration error</li>
          <li>Connection status shows "Disconnected"</li>
        </ul>
        <p className="mb-2 font-semibold">Solutions:</p>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2">
          <li>Check Network:</li>
        </ol>
        {codeBlock(checkNetworkCmd)}
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2" start="2">
          <li>Verify Credentials:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Ensure agent credentials are valid</li>
          <li>Check if nonce is being properly used</li>
          <li>Verify registration endpoint is correct</li>
        </ul>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2" start="3">
          <li>Check Browser Console:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Look for CORS errors</li>
          <li>Check for WebSocket connection errors</li>
          <li>Verify network requests</li>
        </ul>
        <h3 className="font-semibold mt-4 mb-2">2. Audio Issues</h3>
        <p className="mb-2 font-semibold">One-Way Audio (Can hear but can't speak):</p>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2">
          <li>Check Microphone Permissions:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Open browser settings</li>
          <li>Navigate to site permissions</li>
          <li>Ensure microphone access is granted</li>
          <li>Try revoking and re-granting permissions</li>
        </ul>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2" start="2">
          <li>Check Audio Device:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Verify correct microphone is selected</li>
          <li>Test microphone in browser settings</li>
          <li>Try a different microphone if available</li>
        </ul>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2" start="3">
          <li>Browser-Specific Checks:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Chrome: chrome://settings/content/microphone</li>
          <li>Firefox: about:preferences#privacy</li>
          <li>Safari: System Preferences &gt; Security &amp; Privacy &gt; Microphone</li>
        </ul>
        <p className="mb-2 font-semibold">No Audio (Can't hear or speak):</p>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2">
          <li>Check Audio Element:</li>
        </ol>
        {codeBlock(verifyAudioElement)}
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2" start="2">
          <li>Check Volume Levels:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Ensure system volume is up</li>
          <li>Check browser tab is not muted</li>
          <li>Verify volume levels in the UI</li>
        </ul>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2" start="3">
          <li>Browser Audio Settings:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Check if browser is blocking autoplay</li>
          <li>Verify audio output device</li>
          <li>Try different audio output</li>
        </ul>
        <h3 className="font-semibold mt-4 mb-2">3. Connection Issues</h3>
        <p className="mb-2 font-semibold">WebSocket Connection Fails:</p>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2">
          <li>Network Checks:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Verify firewall settings</li>
          <li>Check if WebSocket ports are open</li>
        </ul>
        {codeBlock(websocketTest)}
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2" start="2">
          <li>SSL/TLS Issues:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Ensure HTTPS is used</li>
          <li>Check certificate validity</li>
          <li>Verify SSL/TLS version compatibility</li>
        </ul>
        <p className="mb-2 font-semibold">ICE Connection Fails:</p>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2">
          <li>STUN/TURN Configuration:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Check if STUN/TURN servers are accessible</li>
          <li>Verify network allows UDP traffic</li>
        </ul>
        {codeBlock(stunTest)}
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2" start="2">
          <li>Network Environment:</li>
        </ol>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Check if behind corporate firewall</li>
          <li>Verify VPN settings</li>
          <li>Test with different network</li>
        </ul>
        <h3 className="font-semibold mt-4 mb-2">4. Browser-Specific Issues</h3>
        <p className="mb-2 font-semibold">Chrome:</p>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Clear browser cache and cookies</li>
          <li>Disable extensions temporarily</li>
          <li>Check chrome://webrtc-internals for detailed logs</li>
        </ul>
        <p className="mb-2 font-semibold">Firefox:</p>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Check about:webrtc for connection status</li>
          <li>Verify media permissions</li>
          <li>Test in safe mode</li>
        </ul>
        <p className="mb-2 font-semibold">Safari:</p>
        <ul className="list-disc pl-6 text-sm text-gray-700 mb-2">
          <li>Enable WebRTC in experimental features</li>
          <li>Check system preferences for microphone access</li>
          <li>Verify Safari version compatibility</li>
        </ul>
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">📚 API Reference</h2>
        <p className="mb-4">For detailed API documentation, please refer to our <a href="./API.md" className="text-blue-600 underline">API Reference</a>.</p>
        <h2 className="text-lg font-semibold text-gray-900 mt-6 mb-2">🆘 Need Help?</h2>
        <ol className="list-decimal pl-6 text-sm text-gray-700 mb-2">
          <li>Check the debug logs (enable <code>debug: true</code>)</li>
          <li>Review the troubleshooting guide above</li>
          <li>Collect the following information before contacting support:
            <ul className="list-disc pl-6">
              <li>Browser version and OS</li>
              <li>Debug logs with <code>debug: true</code></li>
              <li>Steps to reproduce the issue</li>
              <li>Network environment details</li>
            </ul>
          </li>
          <li>Contact our support team at <a href="mailto:support@taar.ai" className="text-blue-600 underline">support@taar.ai</a></li>
        </ol>
      </div>
    </main>
  </div>
);

export default WebRTCClient; 